import { pauseGivenStudents, unpauseAllStudents } from "@/api/pause";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { useSelectedClassroomStore } from "@/stores/v2/selectedClassroomStore";
import { useClassroomPolicyStore } from "@/stores/v2/classroomPolicyStore";

export interface StudentBulkActions {
	toUsernames: (qoriaIds: string[]) => string[];
	toStableIds: (qoriaIds: string[]) => string[];
	pauseStudents: (qoriaIds: string[]) => Promise<void>;
	resumeStudents: (qoriaIds: string[]) => Promise<void>;
	resumeAll: () => Promise<void>;
}

export function useStudentBulkActions(): StudentBulkActions {
	const teacherId = useUserSessionStore().getTeacherId() as string;
	const classroomId = useSelectedClassroomStore((s) => s.stableIdV1) as string;
	const students = useSelectedClassroomStore((s) => s.students);

	const byQoria = new Map((students ?? []).map((s) => [s.qoriaUserId, s]));
	const toUsernames = (qoriaIds: string[]): string[] =>
		qoriaIds.map((id) => byQoria.get(id)?.username).filter((v): v is string => Boolean(v));
	const toStableIds = (qoriaIds: string[]): string[] =>
		qoriaIds.map((id) => byQoria.get(id)?.stableIdV1).filter((v): v is string => Boolean(v));

	return {
		toUsernames,
		toStableIds,
		pauseStudents: async (qoriaIds) => {
			const policyStore = useClassroomPolicyStore.getState();
			// The /pause endpoint replaces locked_users with the supplied list, so merge the
			// already-paused students with the newly selected ones to avoid unpausing others.
			const merged = Array.from(new Set([...policyStore.getPausedStudents(), ...toUsernames(qoriaIds)]));
			await pauseGivenStudents(teacherId, classroomId, merged);
			// Update the policy store so cards reflect the paused state without a refresh.
			policyStore.setPausedStudents(merged);
		},
		resumeStudents: async (qoriaIds) => {
			const policyStore = useClassroomPolicyStore.getState();
			const toResume = new Set(toUsernames(qoriaIds));
			// The /pause endpoint replaces locked_users, so resume the selected students by
			// re-pausing only the remaining ones (keeping any other paused students paused).
			const remaining = policyStore.getPausedStudents().filter((u) => !toResume.has(u));
			if (remaining.length === 0) {
				await unpauseAllStudents(teacherId, classroomId);
			} else {
				await pauseGivenStudents(teacherId, classroomId, remaining);
			}
			policyStore.setPausedStudents(remaining);
		},
		resumeAll: async () => {
			await unpauseAllStudents(teacherId, classroomId);
			useClassroomPolicyStore.getState().setPausedStudents([]);
		},
	};
}
